// Convert a MediaRecorder capture to 16kHz mono MP3 (64 kbps), base64-encoded
// for the /ai/transcribe route (dictation format picker, 0020).
//
// 64 kbps mono is plenty for speech (16kHz mono = at most 8kHz of audio
// bandwidth; 64kbps is transparent at that rate) and keeps payloads small:
// 2 min ≈ 960KB raw / 1.28MB base64 — a quarter of WAV.
//
// @breezystack/lamejs is a pure-JS LAME port (~150KB) — dynamically imported
// so it lands in its own chunk and is only paid for when MP3 is selected.

import { decodeToMonoPcm16 } from "./audioToWav";

// lamejs consumes samples in MPEG frame-sized chunks (1152 for MPEG-2.5
// Layer III at 16kHz). Feeding it larger buffers is fine, but chunking keeps
// memory flat and matches the reference usage.
const MP3_CHUNK = 1152;
const BITRATE_KBPS = 64;

export async function audioBlobToMp3Base64(blob: Blob): Promise<{ base64: string; format: string }> {
  const { samples, sampleRate } = await decodeToMonoPcm16(blob);

  // Dynamic import — code-split so non-MP3 users never download lamejs.
  const { Mp3Encoder } = await import("@breezystack/lamejs");
  const encoder = new Mp3Encoder(1, sampleRate, BITRATE_KBPS);

  const parts: Uint8Array[] = [];
  for (let i = 0; i < samples.length; i += MP3_CHUNK) {
    const chunk = samples.subarray(i, i + MP3_CHUNK);
    const encoded = encoder.encodeBuffer(chunk);
    if (encoded.length > 0) parts.push(new Uint8Array(encoded));
  }
  const tail = encoder.flush();
  if (tail.length > 0) parts.push(new Uint8Array(tail));

  const total = parts.reduce((n, p) => n + p.length, 0);
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    bytes.set(p, offset);
    offset += p.length;
  }

  // Uint8Array → base64 (chunked — same pattern as audioToWav.ts).
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return { base64: btoa(binary), format: "mp3" };
}
